import { defineComponent as _defineComponent } from 'vue'
import { AxiosError, AxiosResponse } from 'axios';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import store from '~store';
import ApiErrorMessage from '~widget/ApiErrorMessage';

interface UnzerErrorResponse {
  error: string;
  errors: [];
  fromCC: boolean;
  message: string;
  success: boolean;
  type: string;
}

interface UnzerSuccessResponse {
  id: string;
}

interface UnzerCard {
  shopId: string;
  amount: string;
  currency: string;

  create: (
    field: string,
    {
      containerId,
      onlyIframe,
      fontSize,
      fontColor,
      fontFamily,
    }: {
      containerId: string;
      onlyIframe: boolean;
      fontSize?: string;
      fontColor?: string;
      fontFamily?: string;
    }
  ) => {
    shopId: number;
  }

  createResource: () => this;

  then: (method: (response: UnzerSuccessResponse) => void) => this;

  catch: (method: (error: UnzerErrorResponse) => void) => this;
}

interface UnzerInstance {
  new(key: string): {
    Card: () => UnzerCard
  };
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
declare const unzer: UnzerInstance;


export default /*#__PURE__*/_defineComponent({
  __name: 'UnzerForm',
  props: {
  /* Language as string like "de_DE", "en_GB" or "fr_CH" */
  language: {
    type: String,
    default: null,
  },
  /* Amount to pay as number like 500 */
  amount: {
    type: Number,
    default: null,
  },
  /* Currency as string like "EUR" */
  currency: {
    type: String,
    default: null,
  },
  /* Redeem value. The resulting amount of the paid up voucher for example 2000 */
  redeemValue: {
    type: Number,
    default: null,
  },
  /* Voucher token as long, dot-separated base64 String. */
  voucherToken: {
    type: String,
    default: null,
  },
  /* ID of the shop as a Number like 62 or 1105 */
  shopId: {
    type: Number,
    default: null,
  },
},
  setup(__props) {

const props = __props



// Vue Router
const router = useRouter();

// True on loading ajax requests
const loading = ref(false);

// Errors for the API Error message response component
const errors = ref<object | [] | AxiosError | null>(null);

/*
 * Shows the loading overlay
 */
function showLoader(): void {
  loading.value = true;
}

/*
 * Hides the loading overlay
 */
function hideLoader(): void {
  loading.value = false;
}

/*
 * Init the Unzer payment form for
 * credit card payment.
 */
function initUnzer() {
  // CSS and JS files from unzer
  const unzerAssetsUrl = 'https://static.unzer.com/v1';

  // inject unzer script into body tag
  const script = document.createElement('script');
  script.setAttribute('src', `${unzerAssetsUrl}/unzer.js`);
  script.setAttribute('async', 'async');
  script.setAttribute('data-cookiefirst-category', 'necessary');

  script.addEventListener('load', () => {
    // eslint-disable-next-line new-cap
    const unzerInstance = new unzer(store.getters.getUnzerPublicKey);

    // Creating an Card instance
    const card = unzerInstance.Card();

    // Define default card settings
    const cardDefaultSettings = {
      onlyIframe: false,

      fontSize: '16px',
      fontColor: '#000',

      // eslint-disable-next-line max-len
      fontFamily: `Lato, Roboto, -apple-system, BlinkMacSystemFont,
          "Segoe UI", "Helvetica Neue", "Arial", "Noto Sans", "Liberation Sans",
          sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol",
          "Noto Color Emoji", "Open Sans"`,
    };

    // Rendering input field card number
    card.create('number', {
      ...cardDefaultSettings,
      containerId: 'unzer-credit-card-number',
    });

    // Rendering input field card expiry
    card.create('expiry', {
      ...cardDefaultSettings,
      containerId: 'unzer-credit-card-expiry',
    });

    // Rendering input field card cvc
    card.create('cvc', {
      ...cardDefaultSettings,
      containerId: 'unzer-credit-card-cvc',
    });

    card.create('holder', {
      ...cardDefaultSettings,
      containerId: 'unzer-credit-card-holder',
    });

    card.create('email', {
      ...cardDefaultSettings,
      containerId: 'unzer-credit-card-email',
    });

    // Handling the form submission
    const form = document.getElementById('unzer-payment-form');

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      showLoader();

      // Creating a Card resource
      card
        .createResource()
        .then((result: UnzerSuccessResponse) => {
          const params = {
            resourceId: String(result.id || ''),
            shopId: String(props.shopId || ''),
            amount: String(props.amount || ''),
            currency: String(props.currency || ''),
          };
          const urlSearchParams = new URLSearchParams(params);
          const returnUrl = `${window.location.protocol}//${window.location.host}/payment/unzer?${urlSearchParams.toString()}`;

          store
            .dispatch('initCharge', {
              provider: 'unzer',
              params: {
                resourceId: result.id,
                amount: props.amount,
                returnUrl,
              },
            })
            .then((response: AxiosResponse) => {
              if (response.data.redirectUrl) {
                window.location = response.data.redirectUrl;
              } else {
                router.push({
                  name: 'unzerReturn',
                  params,
                });
              }
            })
            .catch((error: AxiosError) => {
              const { response } = error;
              errors.value = response?.data?.errors || { code: response?.status };
            })
            .then(() => {
              hideLoader();
            });
        })
        .catch((error: UnzerErrorResponse) => {
          hideLoader();
          errors.value = {
            code: error.message,
          };
        });
    });
  });
  document.querySelector('body')?.append(script);

  // inject unzer css into head tag
  const link = document.createElement('link');
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('href', `${unzerAssetsUrl}/unzer.css`);
  document.querySelector('head')?.append(link);
}

onMounted(() => {
  initUnzer();
});

return { __sfc: true,props, router, loading, errors, showLoader, hideLoader, initUnzer, ApiErrorMessage }
}

})